import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import SearchableSelect from "../../../BaseComponents/SearchableSelect/SearchableSelect";
import { useGetCountries } from "../../../../api/geo/useCountries";
import { useGetRegions } from "../../../../api/geo/useRegions";
import { useGetCities } from "../../../../api/geo/useCities";
import { useGetZones } from "../../../../api/geo/useZones";
import styles from "./AddHotelModal.module.css";

const emptyForm = {
  hotelName: "",
  description: "",
  // Geo cascade selections (ids drive each dependent dropdown)
  countryId: "",
  country: "",          // country ISO code → persisted as countryCode
  regionId: "",
  city: "",             // city name → persisted as city
  stateCode: "",        // region code → persisted as stateCode
  cityId: "",
  postalCode: "",
  address: "",
  destinationCode: "",  // city (destination) code → persisted as destinationCode
  zoneId: "",
  zoneCode: "",         // zone code → persisted as zoneCode
  latitude: "",
  longitude: "",
  starRating: 5,
  status: "active",
  contactPerson: "",
  contactEmail: "",
  contactPhone: "",
  web: "",
  accommodationTypeCode: "",
  chainCode: "",
  images: [], // { id, file, previewUrl }
};

const AddHotelModal = ({ open, onClose, onSave, hotel, saving = false }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // ─── Geo cascade data: Country → Region → City → Zone ──────────────────────
  // Each level is filtered by its parent's id; codes are captured for saving.
  const { data: countriesData = [] } = useGetCountries({
    page: 1, limit: 500, sortBy: "name", sortOrder: "ASC",
  });
  const { data: regionsData = [] } = useGetRegions({
    page: 1, limit: 500, countryId: form.countryId || undefined,
    sortBy: "name", sortOrder: "ASC",
  });
  const { data: citiesData = [] } = useGetCities({
    page: 1, limit: 500, countryId: form.countryId || undefined,
    regionId: form.regionId || undefined, sortBy: "name", sortOrder: "ASC",
  });
  const { data: zonesData = [] } = useGetZones({
    page: 1, limit: 500, destinationId: form.cityId || undefined,
    sortBy: "name", sortOrder: "ASC",
  });

  const countries = Array.isArray(countriesData) ? countriesData : [];
  const regions = Array.isArray(regionsData) ? regionsData : [];
  const cities = Array.isArray(citiesData) ? citiesData : [];
  const zones = Array.isArray(zonesData) ? zonesData : [];

  const countryOptions = countries.map((c) => ({
    value: String(c.id),
    label: c.name ? `${c.name}${c.isoCode ? ` (${c.isoCode})` : ""}` : (c.isoCode || c.code || ""),
  }));
  const regionOptions = regions.map((r) => ({
    value: String(r.id),
    label: r.name ? `${r.name}${r.code ? ` (${r.code})` : ""}` : (r.code || ""),
  }));
  const cityOptions = cities.map((c) => ({
    value: String(c.id),
    label: c.name ? `${c.name}${c.code ? ` (${c.code})` : ""}` : (c.code || ""),
  }));
  const zoneOptions = zones.map((z) => ({
    value: String(z.id),
    label: z.name ? `${z.name}${z.zoneCode ? ` (${z.zoneCode})` : ""}` : (z.zoneCode || z.code || ""),
  }));

  // Populate form when editing
  useEffect(() => {
    if (hotel) {
      setForm({
        ...emptyForm,
        hotelName: hotel.hotelName || "",
        description: hotel.description || "",
        city: hotel.city || "",
        country: hotel.country || "",
        stateCode: hotel.stateCode || "",
        postalCode: hotel.postalCode || "",
        address: hotel.address || "",
        destinationCode: hotel.destinationCode || "",
        zoneCode: hotel.zoneCode ?? "",
        latitude: hotel.latitude ?? "",
        longitude: hotel.longitude ?? "",
        starRating: hotel.starRating || 5,
        status: hotel.status || "active",
        contactPerson: hotel.contactPerson || "",
        contactEmail: hotel.contactEmail || "",
        contactPhone: hotel.contactPhone || "",
        web: hotel.web || "",
        accommodationTypeCode: hotel.accommodationTypeCode || "",
        chainCode: hotel.chainCode || "",
        images: [],
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [hotel, open]);

  // Image uploader (local previews; files are uploaded after the hotel is saved)
  const addImages = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const next = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setForm((prev) => ({ ...prev, images: [...prev.images, ...next] }));
  };

  const removeImage = (id) => {
    setForm((prev) => {
      const target = prev.images.find((im) => im.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return { ...prev, images: prev.images.filter((im) => im.id !== id) };
    });
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const clearError = (key) => {
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
  };

  // ─── Cascade handlers: selecting a level resets everything below it ─────────
  const handleCountrySelect = (value) => {
    const c = countries.find((x) => String(x.id) === String(value));
    setForm(prev => ({
      ...prev,
      countryId: value,
      country: c?.isoCode || c?.code || "",
      regionId: "", stateCode: "",
      cityId: "", city: "", destinationCode: "",
      zoneId: "", zoneCode: "",
    }));
    clearError("country");
  };

  const handleRegionSelect = (value) => {
    const r = regions.find((x) => String(x.id) === String(value));
    setForm(prev => ({
      ...prev,
      regionId: value,
      stateCode: r?.code || "",
      cityId: "", city: "", destinationCode: "",
      zoneId: "", zoneCode: "",
    }));
  };

  const handleCitySelect = (value) => {
    const c = cities.find((x) => String(x.id) === String(value));
    setForm(prev => ({
      ...prev,
      cityId: value,
      city: c?.name || "",
      destinationCode: c?.code || "",
      zoneId: "", zoneCode: "",
    }));
    clearError("city");
  };

  const handleZoneSelect = (value) => {
    const z = zones.find((x) => String(x.id) === String(value));
    setForm(prev => ({
      ...prev,
      zoneId: value,
      zoneCode: z?.zoneCode || z?.code || "",
    }));
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "hotelName":
        if (!value || !value.trim()) {
          error = "Hotel name is required";
        } else if (value.trim().length < 3) {
          error = "Hotel name must be at least 3 characters";
        }
        break;
      case "city":
        if (!value || !value.trim()) {
          error = "City is required";
        }
        break;
      case "country":
        if (!value) {
          error = "Country is required";
        }
        break;
      case "address":
        if (!value || !value.trim()) {
          error = "Address is required";
        }
        break;
      case "contactPerson":
        if (!value || !value.trim()) {
          error = "Contact person is required";
        }
        break;
      case "contactEmail":
        if (!value || !value.trim()) {
          error = "Contact email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Invalid email format";
        }
        break;
      case "contactPhone":
        if (!value || !value.trim()) {
          error = "Contact phone is required";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    // Validate all required fields
    const fieldsToValidate = ["hotelName", "city", "country", "address", "contactPerson", "contactEmail", "contactPhone"];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, form[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    if (saving) return;
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    if (saving) return;
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    const payload = {
      hotelName: form.hotelName.trim(),
      description: form.description.trim(),
      city: form.city.trim(),
      country: form.country,
      stateCode: form.stateCode.trim(),
      postalCode: form.postalCode.trim(),
      address: form.address.trim(),
      destinationCode: form.destinationCode.trim(),
      zoneCode: form.zoneCode,
      latitude: form.latitude,
      longitude: form.longitude,
      starRating: form.starRating,
      contactPerson: form.contactPerson.trim(),
      contactEmail: form.contactEmail.trim().toLowerCase(),
      contactPhone: form.contactPhone.trim(),
      web: form.web.trim(),
      accommodationTypeCode: form.accommodationTypeCode.trim(),
      chainCode: form.chainCode.trim(),
      status: form.status,
      // Raw File objects — uploaded by the caller after the hotel record exists.
      images: form.images.map((im) => im.file),
    };

    await onSave(payload);
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={saving ? () => {} : handleClose}
      title={hotel ? "Edit Hotel" : "Add New Hotel"}
      width="800px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose} disabled={saving}>
             Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
             {saving ? "Saving..." : "Save Hotel"}
          </button>
        </>
      }
    >
      {/* Icon Header */}
      <div className={styles.iconHeader}>
        <div className={styles.icon}>🏨</div>
        <div className={styles.headerText}>
          <h3>{hotel ? "Edit Hotel Details" : "Add New Hotel"}</h3>
          <p>
            {hotel 
              ? "Update hotel master data for bookings and inventory management"
              : "Create a new hotel entry with complete contact information and star rating"}
          </p>
        </div>
      </div>

      {/* Validation Error Banner */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) => 
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Basic Information Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🏢 BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>HOTEL NAME <span className={styles.required}>*</span></label>
            <input
              value={form.hotelName}
              onChange={e => handleChange("hotelName", e.target.value)}
              onBlur={() => handleBlur("hotelName")}
              placeholder="e.g., Burj Al Arab Jumeirah"
              className={errors.hotelName ? styles.inputError : ""}
            />
            {errors.hotelName && (
              <span className={styles.errorText}>{errors.hotelName}</span>
            )}
            <span className={styles.helpText}>
              Full official name of the hotel property
            </span>
          </div>

          <div className={styles.field}>
            <label>STAR RATING <span className={styles.required}>*</span></label>
            <div className={styles.starRatingPicker}>
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  className={`${styles.starBtn} ${form.starRating >= rating ? styles.starActive : ""}`}
                  onClick={() => handleChange("starRating", rating)}
                >
                  ⭐
                </button>
              ))}
              <span className={styles.ratingText}>{form.starRating} Star</span>
            </div>
            <span className={styles.helpText}>
              Official hotel classification rating
            </span>
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <div className={styles.statusToggle}>
              <input
                type="checkbox"
                id="hotelStatus"
                checked={form.status === "active"}
                onChange={e => handleChange("status", e.target.checked ? "active" : "inactive")}
                className={styles.toggleInput}
              />
              <label htmlFor="hotelStatus" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch}></span>
                <span className={styles.toggleText}>
                  {form.status === "active" ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
            <span className={styles.helpText}>
              Only active hotels appear in bookings
            </span>
          </div>

          <div className={styles.fieldWide}>
            <label>DESCRIPTION</label>
            <textarea
              value={form.description}
              onChange={e => handleChange("description", e.target.value)}
              placeholder="Short description of the hotel property"
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📍 LOCATION DETAILS</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>COUNTRY <span className={styles.required}>*</span></label>
            <SearchableSelect
              value={form.countryId}
              onChange={handleCountrySelect}
              onBlur={() => handleBlur("country")}
              options={countryOptions}
              placeholder={countryOptions.length ? "Select country" : "Loading countries…"}
              searchPlaceholder="Search country…"
              error={!!errors.country}
            />
            {errors.country && (
              <span className={styles.errorText}>{errors.country}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>REGION</label>
            <SearchableSelect
              value={form.regionId}
              onChange={handleRegionSelect}
              options={regionOptions}
              placeholder={form.countryId ? "Select region (optional)" : "Select a country first"}
              searchPlaceholder="Search region…"
              disabled={!form.countryId}
            />
            <span className={styles.helpText}>
              Regions of the selected country
            </span>
          </div>

          <div className={styles.field}>
            <label>CITY <span className={styles.required}>*</span></label>
            <SearchableSelect
              value={form.cityId}
              onChange={handleCitySelect}
              onBlur={() => handleBlur("city")}
              options={cityOptions}
              placeholder={form.countryId ? "Select city" : "Select a country first"}
              searchPlaceholder="Search city…"
              disabled={!form.countryId}
              error={!!errors.city}
            />
            {errors.city && (
              <span className={styles.errorText}>{errors.city}</span>
            )}
            <span className={styles.helpText}>
              Cities in the selected country{form.regionId ? " / region" : ""}
            </span>
          </div>

          <div className={styles.field}>
            <label>ZONE</label>
            <SearchableSelect
              value={form.zoneId}
              onChange={handleZoneSelect}
              options={zoneOptions}
              placeholder={form.cityId ? "Select zone (optional)" : "Select a city first"}
              searchPlaceholder="Search zone…"
              disabled={!form.cityId}
            />
            <span className={styles.helpText}>
              Zones within the selected city
            </span>
          </div>

          <div className={styles.field}>
            <label>DESTINATION CODE</label>
            <input
              value={form.destinationCode}
              readOnly
              placeholder="Auto-filled from city"
              className={styles.readOnlyInput}
            />
            <span className={styles.helpText}>
              Derived from the selected city
            </span>
          </div>

          <div className={styles.field}>
            <label>POSTAL CODE</label>
            <input
              value={form.postalCode}
              onChange={e => handleChange("postalCode", e.target.value)}
              placeholder="e.g., 77310"
            />
          </div>

          <div className={styles.fieldWide}>
            <label>ADDRESS <span className={styles.required}>*</span></label>
            <input
              value={form.address}
              onChange={e => handleChange("address", e.target.value)}
              onBlur={() => handleBlur("address")}
              placeholder="e.g., Jumeirah Street, Umm Suqeim 3"
              className={errors.address ? styles.inputError : ""}
            />
            {errors.address && (
              <span className={styles.errorText}>{errors.address}</span>
            )}
            <span className={styles.helpText}>
              Complete street address of the hotel
            </span>
          </div>

          <div className={styles.field}>
            <label>LATITUDE</label>
            <input
              type="number"
              step="any"
              value={form.latitude}
              onChange={e => handleChange("latitude", e.target.value)}
              placeholder="e.g., 21.5159325"
            />
          </div>

          <div className={styles.field}>
            <label>LONGITUDE</label>
            <input
              type="number"
              step="any"
              value={form.longitude}
              onChange={e => handleChange("longitude", e.target.value)}
              placeholder="e.g., -87.3928160"
            />
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📞 CONTACT INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>CONTACT PERSON <span className={styles.required}>*</span></label>
            <input
              value={form.contactPerson}
              onChange={e => handleChange("contactPerson", e.target.value)}
              onBlur={() => handleBlur("contactPerson")}
              placeholder="e.g., Ahmed Hassan"
              className={errors.contactPerson ? styles.inputError : ""}
            />
            {errors.contactPerson && (
              <span className={styles.errorText}>{errors.contactPerson}</span>
            )}
            <span className={styles.helpText}>
              Primary contact person for reservations
            </span>
          </div>

          <div className={styles.field}>
            <label>CONTACT EMAIL <span className={styles.required}>*</span></label>
            <input
              type="email"
              value={form.contactEmail}
              onChange={e => handleChange("contactEmail", e.target.value)}
              onBlur={() => handleBlur("contactEmail")}
              placeholder="reservations@hotel.com"
              className={errors.contactEmail ? styles.inputError : ""}
            />
            {errors.contactEmail && (
              <span className={styles.errorText}>{errors.contactEmail}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>CONTACT PHONE <span className={styles.required}>*</span></label>
            <input
              type="tel"
              value={form.contactPhone}
              onChange={e => handleChange("contactPhone", e.target.value)}
              onBlur={() => handleBlur("contactPhone")}
              placeholder="+971 4 301 7777"
              className={errors.contactPhone ? styles.inputError : ""}
            />
            {errors.contactPhone && (
              <span className={styles.errorText}>{errors.contactPhone}</span>
            )}
          </div>

          <div className={styles.fieldWide}>
            <label>WEBSITE</label>
            <input
              type="url"
              value={form.web}
              onChange={e => handleChange("web", e.target.value)}
              placeholder="https://www.hotel.com"
            />
          </div>
        </div>
      </div>

      {/* Additional Details Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🏷️ ADDITIONAL DETAILS</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>ACCOMMODATION TYPE</label>
            <input
              value={form.accommodationTypeCode}
              onChange={e => handleChange("accommodationTypeCode", e.target.value)}
              placeholder="e.g., HOTEL"
            />
          </div>
          <div className={styles.field}>
            <label>CHAIN CODE</label>
            <input
              value={form.chainCode}
              onChange={e => handleChange("chainCode", e.target.value)}
              placeholder="e.g., MARRIOTT"
            />
          </div>
        </div>
      </div>

      {/* Images Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🖼️ HOTEL IMAGES</h4>
        <div
          style={{
            border: "2px dashed #cbd5e1", borderRadius: 10, padding: "18px",
            textAlign: "center", cursor: "pointer", background: "#f8fafc",
          }}
          onClick={() => document.getElementById("hotelImageInput")?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addImages(e.dataTransfer.files); }}
        >
          <input
            id="hotelImageInput"
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            multiple
            style={{ display: "none" }}
            onChange={(e) => { addImages(e.target.files); e.target.value = ""; }}
          />
          <div style={{ fontSize: 22, marginBottom: 4 }}>⬆️</div>
          <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>Click to upload or drag &amp; drop</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>PNG, JPG, WEBP — up to 5MB each. Uploaded after the hotel is saved.</div>
        </div>

        {form.images.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
            {form.images.map((im) => (
              <div key={im.id} style={{ position: "relative", width: 96, height: 72, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                <img src={im.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  type="button"
                  onClick={() => removeImage(im.id)}
                  style={{
                    position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%",
                    border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer", lineHeight: "18px",
                  }}
                  title="Remove image"
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Business Rules Notice */}
      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Hotels are master data foundation for room mapping and inventory</li>
            <li>Complete contact information is required for all bookings</li>
            <li>Star rating determines pricing tier and customer search filters</li>
            <li>Inactive hotels won't appear in booking search results</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddHotelModal;